import { useReducer } from "react";
import LandLordContext from "./LandLordContext";
import LandLordReducer from "./LandLordReducer";

import {
  ERROR_RESOLVE,
  LANDLORD_LOADED,
  LOAD_PROPERTY,
  PROPERTY_ADDED,
  PROPERTY_DELETED,
  PROPERTY_EDITED,
  PROPERTY_ERROR,
  PROPERTY_LOADED,
  SET_PROPERTY,
  TOGGLE_LOADING,
  IS_PARENT_LL,
  LOAD_POLICY_DETAILS,
  LOAD_PROPERTY_ALL,
  SET_DEA_WORKSPACE,
} from "../types";
import "react-toastify/dist/ReactToastify.css";
import useTranslator from "../../hooks/useTranslator";
import { haveValue, toast } from "../../helper/common";
import axiosInstance from "../../services/authWrapper";

const LandLordState = (props) => {
  const { _t } = useTranslator();
  const initialState = {
    fullProfileReceived: [],
    basicProfileReceived: [],
    properties: [],
    propertiesAll: [],
    landlords: [],
    current_property: null,
    landlord_loading: false,
    landlord_error: null,
    isParentLL: false,
    deaWorkspace: false,
    customPolicyDetails: {
      overlayConfig: {},
      policyConfig: {},
      scoringConfig: {},
    },
  };

  const [state, dispatch] = useReducer(LandLordReducer, initialState);

  const setLandlordLoading = (value) => {
    dispatch({
      type: TOGGLE_LOADING,
      payload: value,
    });
  };

  const setErrorResolve = () => {
    dispatch({
      type: ERROR_RESOLVE,
    });
  };

  const loadAllLandlords = async () => {
    await axiosInstance
      .get(`${process.env.REACT_APP_SERVER_URL}/landlords/alllandlords`, {
        headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
      })
      .then((res) => {
        dispatch({
          type: LANDLORD_LOADED,
          payload: res.data.landlords,
        });
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  const setProperties = (properties) => {
    dispatch({
      type: PROPERTY_LOADED,
      payload: properties,
    });
  };

  const loadProperties = async (landlordid, data = {}) => {
    setLandlordLoading(true);

    // Construct query parameters from the data object
    const queryParams = new URLSearchParams(data).toString();

    await axiosInstance
      .get(
        `${process.env.REACT_APP_SERVER_URL}/properties/${landlordid}?${queryParams}`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      )
      .then((res) => {
        let server_response = res;
        dispatch({
          type: LOAD_PROPERTY_ALL,
          payload: server_response.data.properties,
        });
        dispatch({
          type: LOAD_PROPERTY,
          payload: server_response.data.properties,
        });
        dispatch({
          type: IS_PARENT_LL,
          payload: server_response.data.isParentLL,
        });
      })
      .catch((err) => {
        let server_error = err;
        dispatch({
          type: PROPERTY_ERROR,
          payload: server_error.message,
        });
      });
  };

  const loadPropertiesNew = async (landlordid, data = {}) => {
    try {
      setLandlordLoading(true);

      // Construct query parameters from the data object
      const queryParams = new URLSearchParams(data).toString();

      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/properties/get/${landlordid}?${queryParams}`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );

      dispatch({
        type: LOAD_PROPERTY,
        payload: res.data.properties,
      });
      dispatch({
        type: IS_PARENT_LL,
        payload: res.data.isParentLL,
      });

      return res.data; // 👈 now the function actually returns data
    } catch (err) {
      dispatch({
        type: PROPERTY_ERROR,
        payload: err.message,
      });
      throw err; // 👈 propagate error if needed
    } finally {
      setLandlordLoading(false);
    }
  };

  const setCurrentProperty = (property) => {
    dispatch({
      type: SET_PROPERTY,
      payload: property,
    });
  };

  const addProperty = async (landlordid, property) => {
    setLandlordLoading(true);

    await axiosInstance
      .post(
        `${process.env.REACT_APP_SERVER_URL}/properties/${landlordid}`,
        property,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        let server_response = res;
        toast.success(_t("propertySaved"));
        dispatch({
          type: PROPERTY_ADDED,
          payload: server_response.data.properties,
        });
      })
      .catch((err) => {
        let server_error = err;
        toast.error(server_error.message);
        dispatch({
          type: PROPERTY_ERROR,
          payload: server_error.message,
        });
      });
  };

  const editProperty = async (propertyid, property) => {
    setLandlordLoading(true);
    await axiosInstance
      .patch(
        `${process.env.REACT_APP_SERVER_URL}/properties/${propertyid}`,
        property,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        toast.success(_t("propertyInfoChanged"));
      })
      .catch((err) => {
        let server_error = err;
        toast.error(
          server_error?.response?.data?.msg ?? _t("failedToEditProperty")
        );
      });
  };

  const deleteProperty = async (propertyid, landlordid) => {
    setLandlordLoading(true);
    await axiosInstance
      .delete(
        `${process.env.REACT_APP_SERVER_URL}/properties/${propertyid}/${landlordid}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        toast.success(_t("propertyDeleted"));
        dispatch({
          type: PROPERTY_DELETED,
          payload: propertyid,
        });
      })
      .catch((err) => {
        let server_error = err;
        toast.error(_t("notAuthorizedToDeleteProperty"));
        dispatch({
          type: PROPERTY_ERROR,
          payload: server_error.message,
        });
      });
  };

  const requestFullProfile = async (
    landlordid,
    tenantid,
    sharedprofileid,
    propertypayload
  ) => {
    await axiosInstance
      .post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/request-profile/${landlordid}/${tenantid}/${sharedprofileid}`,
        propertypayload,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        let serverResponse = res;
        if (serverResponse.data.status == "success") {
          toast.success(_t("fullProfileRequestSentToTenant"));
        }
      })
      .catch((err) => {
        toast.error("Error", err.response);
      });
  };

  const loadProfilesLandlord = async (tenantid, profiletype, data = {}) => {
    const queryParams = new URLSearchParams(data).toString();

    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/landlord-received/${tenantid}?profiletype=${profiletype}&${queryParams}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      const serverResponse = res.data ?? {
        data: [],
        total: 0,
        page: data?.page ?? 1,
        limit: data?.limit ?? 15,
      };

      // if (profiletype === "full") {
      //   dispatch({
      //     type: FULLSHARED_LOADED,
      //     payload: serverResponse,
      //   });
      // }

      // if (profiletype === "basic") {
      //   dispatch({
      //     type: BASICSHARED_LOADED,
      //     payload: serverResponse,
      //   });
      // }

      // setLandlordLoading(false);
      return serverResponse; // ✅ returned properly
    } catch (err) {
      // setLandlordLoading(false);

      // dispatch({
      //   type: SHARED_PROFILE_FAILED_LOADING,
      //   payload: err,
      // });

      // return {
      //   success: false,
      //   data: [],
      //   total: 0,
      //   page: data?.page ?? 1,
      //   limit: data?.limit ?? 15,
      // };
      throw err;
    }
  };

  const getAutoCompleteList = async (keywords) => {
    const resp = await axiosInstance.get(
      `${process.env.REACT_APP_SERVER_URL}/landlords/getAutoComplete?keywords=${keywords}`,
      { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
    );
    var serverResponse = resp.data;
    if (
      haveValue(resp?.data?.data?.from_ref) &&
      resp?.data?.data?.from_ref == true
    ) {
      return serverResponse.data;
    } else {
      return serverResponse.data.results;
    }
  };

  const validateCadastre = async (code_ine, cod_via, numero_via) => {
    const resp = await axiosInstance.get(
      `${process.env.REACT_APP_SERVER_URL}/landlords/validateCadastre?code_ine=${code_ine}&cod_via=${cod_via}&numero_via=${numero_via}`,
      { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
    );
    let serverResponse = resp.data;
    return serverResponse.data.results;
  };

  const getBuildingData = async (reference, cod_via, numero_via) => {
    const resp = await axiosInstance.get(
      `${process.env.REACT_APP_SERVER_URL}/landlords/getBuildingData?reference=${reference}&cod_via=${cod_via}&numero_via=${numero_via}`,
      { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
    );
    let serverResponse = resp.data;
    return serverResponse.data.results;
  };

  const editSharedProfile = async (sharedprofileid, data) => {
    setLandlordLoading(true);
    await axiosInstance
      .patch(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/editShared/${sharedprofileid}`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        let server_response = res;
        // loadProperties(landlordid);
        dispatch({
          type: PROPERTY_EDITED,
          payload: server_response.data.property,
        });
      })
      .catch((err) => {
        let server_error = err;
        toast.error(server_error.message);
      });
  };
  const editAgencySharedProfile = async (sharedprofileid, data) => {
    setLandlordLoading(true);
    await axiosInstance
      .patch(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/editAgencyShared/${sharedprofileid}`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        let server_response = res;
        dispatch({
          type: PROPERTY_EDITED,
          payload: server_response.data.property,
        });
      })
      .catch((err) => {
        let server_error = err;
        toast.error(server_error.message);
      });
  };

  const getPropertyDetails = async (property_id = null) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/properties/property-info/${property_id}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const getSharedProfile = async (sharedprofileid, landlordId) => {
    const url = landlordId
      ? `${process.env.REACT_APP_SERVER_URL}/shared-profile/singleshared/${sharedprofileid}?landlordId=${landlordId}`
      : `${process.env.REACT_APP_SERVER_URL}/shared-profile/singleshared/${sharedprofileid}`;
    try {
      const response = await axiosInstance.get(url, {
        headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
      });
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const changeIncomeSource = async (id, payload) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/changeIncomeSource/${id}`,
        payload,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const disableIncomeSource = async (id, payload) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/disableIncomeSource/${id}`,
        payload,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const changeFinancialIncomeSource = async (id, payload) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/changeFinancialIncomeSource/${id}`,
        payload,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const updateOpenAtTenantProfile = async (id, payload) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/updateOpenedAt/${id}`,
        payload,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const initiateAssignProperty = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/initiateAssignProperty`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const fetchAssignedProperties = async (landlordId) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/load/assigned/properties/${landlordId}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const fetchAssignedDetails = async (id) => {
    try {
      if (haveValue(id)) {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/landlords/load/assigned/details/${id}`,
          {
            headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
          }
        );
        return response.data;
      } else {
        return false;
      }
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const loadRentLeger = async (id) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/load/rent/ledger/${id}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const endLease = async (assignPropId) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_SERVER_URL}/landlords/end/lease/${assignPropId}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const saveRentPercentage = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/save/rent/percentage`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const updatePropertyDetails = async (property_id, data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/properties/update-property-info/${property_id}`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const sendAdminApprovalRequest = async (landlord_id) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/send/for/approval`,
        { landlord_id },
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      toast.success(_t("approvalRequestSentToAdmin"));
    } catch (err) {
      console.log(err);
    }
  };

  const addSubLL = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/add/subLandlord`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      toast.success(_t("subLLAdded"));
      return response;
    } catch (err) {
      const message = err?.response?.data?.errormsg;
      if (haveValue(message)) {
        toast.error(message);
      }
      console.log(err, "in catch");
    }
  };
  const getSubLL = async (data) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/get/subLandlord`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response;
    } catch (err) {
      console.log(err, "in catch");
    }
  };

  const getWorkspaceDetails = async (landlord_id) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/users/get/workspacedata/${landlord_id}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response;
    } catch (err) {
      return { success: false, data: null };
    }
  };
  const getWorkspaceDetailsFromSlug = async (
    workspace_slug,
    workspace_uuid
  ) => {
    try {
      let url = `${process.env.REACT_APP_SERVER_URL}/users/workspacedatainfo`;

      if (workspace_slug) {
        url += `?workspace_slug=${workspace_slug}`;
      } else if (workspace_uuid) {
        url += `?workspace_uuid=${workspace_uuid}`;
      }

      const response = await axiosInstance.get(url, {
        headers: {
          "Accept-Language": localStorage.getItem("i18nextLng"),
        },
      });

      return response;
    } catch (err) {
      return { success: false, data: null };
    }
  };
  const getAgencyAgents = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/get/agents`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response;
    } catch (err) {
      return { success: false, data: null };
    }
  };
  const getAgencyDetails = async () => {
    try {
      if (haveValue(localStorage.getItem("token"))) {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/landlords/agency/details`,
          {
            headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
          }
        );
        return response;
      }
    } catch (err) {
      return { success: false, data: null };
    }
  };
  const alreadySharedSubLL = async (tenant_id) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/already/shared/subLL`,
        {
          tenant_id,
        },
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response;
    } catch (err) {
      return { success: false, data: null };
    }
  };
  const loadAgencyProfile = async (
    agency_id,
    page = 1,
    limit = 10,
    searchVal = "",
    llCreatedOnly = false
  ) => {
    const data = { searchVal, page, limit, llCreatedOnly };

    const queryParams = new URLSearchParams(data).toString();

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/shared-profile/agency/profiles/${agency_id}?${queryParams}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      const serverResponse = response.data ?? {
        data: [],
        total: 0,
        page,
        limit,
      };

      // dispatch({
      //   type: FULLSHARED_LOADED,
      //   payload: serverResponse,
      // });
      // setLandlordLoading(false);

      return serverResponse;
    } catch (err) {
      return { success: false, data: [], total: 0, page, limit };
    }
  };
  const sendInvitationToTenant = async (data) => {
    return await axiosInstance.post(
      `${process.env.REACT_APP_SERVER_URL}/landlords/send-invitation-to-tenant`,
      {
        ...data,
      },
      { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
    );
  };

  const getTanentData = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/get-tanent`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return { success: false, data: null };
    }
  };

  const saveTanentData = async (data) => {
    return await axiosInstance.post(
      `${process.env.REACT_APP_SERVER_URL}/landlords/save-tanent`,
      {
        ...data,
      },
      { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
    );
  };

  const createTenant = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/create-tenant`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return err;
    }
  };

  const createLLGuarantor = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/create-ll-guarantor`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return err;
    }
  };

  const updateLLTenant = async (data) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/update-tenant`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return err;
    }
  };

  const updateAdditionalInfo = async (data) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/tenantchecks/extrafields`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return err;
    }
  };

  const fetchCreatedTenant = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/fetch/created-tenants`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return err;
    }
  };
  const fetchingCreatedTenant = async (req_id) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/fetch/created-tenant/${req_id}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return err;
    }
  };

  const updateCreatedTenant = async (data) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/create-tenant`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      return response.data;
    } catch (err) {
      return err;
    }
  };

  const updateRentDepositReport = async (property_id, data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/properties/update-report-rent/${property_id}`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const initiateInsuracePayment = async (property_id, data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/properties/initiate-insurace-payment/${property_id}`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const checkForInsuranceEligibility = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/check/for/insurance`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return err.response?.data;
    }
  };

  const runInsuranceSimulation = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/run/insurance/simulation`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return err.response?.data;
    }
  };

  const checkMutuaConsents = async (tenantIds) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/check/mutua/consents`,
        { tenantIds },
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return err.response?.data;
    }
  };

  const multiPropAward = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/users/multi/award/property`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };
  const multiPropUnAward = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/users/unaward/property`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const getTinkReportData = async (tenantId) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/getTinkReportData?tenant_id=${tenantId}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      toast.error("Error while fetching Tink Report");
    }
  };

  const addCredit = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/payment/add/credits`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };

  const sendNoteToTenant = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/sendNoteEmailToTenant`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
    }
  };
  const updateExtraFields = async (formData) => {
    await axiosInstance
      .patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/update/extra`,
        { id: localStorage.getItem("saveduserid"), formData },
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        let server_error = err;
        toast.error(server_error.message);
      });
  };

  const bringPropertiesKelify = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/properties/import-property`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      return err;
    }
  };

  const sendFinancialInfoRemainder = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/remainder/financialSkor`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      return err;
    }
  };

  const loadCreditsLedgerData = async (data = {}) => {
    const queryParams = new URLSearchParams(data).toString();

    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/getCreditsLedger?${queryParams}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      const serverResponse = res.data ?? {
        data: [],
        total: 0,
        page: data?.page ?? 1,
        limit: data?.limit ?? 15,
      };

      return serverResponse;
    } catch (err) {
      return {
        success: false,
        data: [],
        total: 0,
        page: data?.page ?? 1,
        limit: data?.limit ?? 15,
      };
    }
  };

  const sendRemoteConnectLink = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/sendRemoteConnectLink`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      return err;
    }
  };

  const getShareProfilesForProperty = async (propertyId) => {
    if (!propertyId) {
      return;
    }
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/properties/getShareProfiles/${propertyId}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      return err;
    }
  };

  const getPropertiesData = async (userId) => {
    if (!userId) {
      return;
    }
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/getLinkedPropertyData/${userId}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      return null;
    }
  };

  const uploadFileData = async (filedata) => {
    try {
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/uploadDocument`,
        filedata,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
            "x-access-token": localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return res;
    } catch (err) {
      toast.error(_t("errorInUploadingFile"));
    }
  };

  const addNewWorkspace = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/create-workspace`,
        data,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const deleteWorkspace = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_SERVER_URL}/landlords/delete-workspace`,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };
  const updateWorkspace = async (data) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/update-workspace`,
        data,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateInvitation = async (data) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/update-invitation`,
        data,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const getBasicWorkspaceDetails = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/get/workspace/basic/details`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const acceptWorkspaceInvite = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/accept/workspace/invite`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const declineWorkspaceInvite = async (data) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_SERVER_URL}/landlords/decline/workspace/invite`,
        {
          data,
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const addToWorkspace = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/add/to/workspace`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };
  const inviteWorkspaceMembers = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/send/member/invite`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const removeFromWorkspace = async (data) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_SERVER_URL}/landlords/remove/from/workspace`,
        {
          data,
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const getWorkspaceMembers = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/get/workspace/members`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateWorkspaceRoles = async (data) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/update/workspace/member`,
        data,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const deleteWorkspaceMember = async (data) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_SERVER_URL}/landlords/update/workspace/member`,
        {
          data,
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const getGuestToken = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/users/get/guest/token`,
        data,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchAllLandlordNotification = async (page = 1) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/notifications/get-all-landlord-notifications?page=${page}`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchWorkspacesOverview = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/workspaces/overview`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchWorkspaceInvites = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/fetch/pending/workspace/invites`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const tourCompleted = async () => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/tourCompleted`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const checkForDuplicateInvitationSlug = async (data) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/checkforduplicateinvitationslug`,
        data,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const deleteInvitationLogo = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_SERVER_URL}/landlords/removeInvitationLogo`,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };
  const getPolicyDetails = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/fetch/policy/details`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      dispatch({
        type: LOAD_POLICY_DETAILS,
        payload: response.data.success_res,
      });
    } catch (err) {
      console.log(err);
    }
  };

  const getTenantExtraData = async (sharedProfileIds) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/get/tenant/extrafields`,
        { sharedProfileIds },
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const isCustomClientWorkspace = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/is/custom/client/workspace`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      dispatch({
        type: SET_DEA_WORKSPACE,
        payload: response.data.success_res.isDeaWorkspace ?? false,
      });

      return response?.data?.success_res?.isDeaWorkspace ?? false;
    } catch (err) {
      console.log(err);
      dispatch({
        type: SET_DEA_WORKSPACE,
        payload: false,
      });
      return false;
      // throw err;
    }
  };

  const getRiskAssessmentReport = async (reportData) => {
    if (!reportData) {
      return;
    }
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/tenants/risk-assessment-report`,
        reportData,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      return err;
    }
  };

  const sendAddGuarantorRecommendationEmail = async (data) => {
    if (!data?.tenantId) return;
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/landlords/send/add/guarantor/recommendation/email`,
        data,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      return err;
    }
  };

  // const isDeaWorkspace = async (email) => {
  //   try {
  //     let isDeaDomain = false;
  //     if (email?.endsWith("@deacapital.com")) {
  //       isDeaDomain = true;
  //     }
  //     dispatch({
  //       type: SET_DEA_WORKSPACE,
  //       payload: isDeaDomain,
  //     });
  const getWorkspaceMemberData = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/landlords/get/workspace/member/data`,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );

      return response?.data?.success_res;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateWorkspaceEmailPreference = async (payload) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/landlords/update/workspace/member/email-preference`,
        payload,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );

      return response?.data?.success_res;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const checkInsuranceDocumentStatus = async (property_id = null) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/properties/checkPolicyDocumentStatus/${property_id}`,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  };

  return (
    <LandLordContext.Provider
      value={{
        properties: state.properties,
        propertiesAll: state.propertiesAll,
        current_property: state.current_property,
        landlord_error: state.landlord_error,
        landlord_loading: state.landlord_loading,
        fullProfileReceived: state.fullProfileReceived,
        basicProfileReceived: state.basicProfileReceived,
        isParentLL: state.isParentLL,
        customPolicyDetails: state.customPolicyDetails,
        deaWorkspace: state.deaWorkspace,
        loadAllLandlords,
        setProperties,
        loadProperties,
        setCurrentProperty,
        addProperty,
        editProperty,
        deleteProperty,
        setLandlordLoading,
        requestFullProfile,
        setErrorResolve,
        loadProfilesLandlord,
        getAutoCompleteList,
        validateCadastre,
        getBuildingData,
        editSharedProfile,
        getPropertyDetails,
        getSharedProfile,
        initiateAssignProperty,
        fetchAssignedProperties,
        fetchAssignedDetails,
        loadRentLeger,
        endLease,
        saveRentPercentage,
        updatePropertyDetails,
        sendAdminApprovalRequest,
        addSubLL,
        getSubLL,
        getWorkspaceDetails,
        getWorkspaceDetailsFromSlug,
        getAgencyAgents,
        alreadySharedSubLL,
        loadAgencyProfile,
        getAgencyDetails,
        editAgencySharedProfile,
        sendInvitationToTenant,
        getTanentData,
        saveTanentData,
        createTenant,
        fetchCreatedTenant,
        fetchingCreatedTenant,
        updateCreatedTenant,
        updateRentDepositReport,
        multiPropAward,
        changeIncomeSource,
        changeFinancialIncomeSource,
        getTinkReportData,
        updateOpenAtTenantProfile,
        addCredit,
        loadPropertiesNew,
        sendNoteToTenant,
        disableIncomeSource,
        updateExtraFields,
        bringPropertiesKelify,
        sendFinancialInfoRemainder,
        loadCreditsLedgerData,
        createLLGuarantor,
        updateLLTenant,
        updateAdditionalInfo,
        sendRemoteConnectLink,
        multiPropUnAward,
        getShareProfilesForProperty,
        getPropertiesData,
        getRiskAssessmentReport,
        initiateInsuracePayment,
        checkForInsuranceEligibility,
        checkMutuaConsents,
        uploadFileData,
        addNewWorkspace,
        deleteWorkspace,
        updateWorkspace,
        getBasicWorkspaceDetails,
        acceptWorkspaceInvite,
        addToWorkspace,
        removeFromWorkspace,
        getWorkspaceMembers,
        inviteWorkspaceMembers,
        updateWorkspaceRoles,
        deleteWorkspaceMember,
        getGuestToken,
        fetchWorkspaceInvites,
        declineWorkspaceInvite,
        tourCompleted,
        fetchAllLandlordNotification,
        fetchWorkspacesOverview,
        updateInvitation,
        checkForDuplicateInvitationSlug,
        deleteInvitationLogo,
        getPolicyDetails,
        getTenantExtraData,
        isCustomClientWorkspace,
        getWorkspaceMemberData,
        updateWorkspaceEmailPreference,
        checkInsuranceDocumentStatus,
        sendAddGuarantorRecommendationEmail,
        runInsuranceSimulation,
      }}
    >
      {props.children}
    </LandLordContext.Provider>
  );
};

export default LandLordState;
